// ---------------------------------------------------------------------------
// Theme hook — aligned to the shared site theme contract
// ---------------------------------------------------------------------------
import { useState, useEffect, useCallback } from "react";
import {
  buildSimpleSiteTheme,
  SITE_THEME_PRESETS,
  SIMPLE_EDITOR_DEFAULTS,
  normalizeSiteTheme,
  sanitizeSiteTheme,
  type BrandColors,
  type BrandFonts,
  type SiteTheme,
} from "@ease/page-builder";
import { apiFetch } from "@/lib/api-client";

export { SITE_THEME_PRESETS };
export type { SiteTheme };

export const FONT_MOOD_OPTIONS = [
  {
    id: "clean",
    label: "Clean",
    description: "Clear, modern, and easy to trust.",
    presetId: "modern",
  },
  {
    id: "elegant",
    label: "Elegant",
    description: "Refined serif headlines with a softer tone.",
    presetId: "classic",
  },
  {
    id: "confident",
    label: "Confident",
    description: "Bold, high-contrast, and campaign-ready.",
    presetId: "bold",
  },
  {
    id: "calm",
    label: "Calm",
    description: "Minimal, airy, and quietly premium.",
    presetId: "minimal",
  },
  {
    id: "friendly",
    label: "Friendly",
    description: "Rounded, warm, and approachable.",
    presetId: "playful",
  },
] as const;

export type FontMoodId = (typeof FONT_MOOD_OPTIONS)[number]["id"];

interface QuickThemeInput {
  fontMoodId?: FontMoodId;
  templateVertical?: string | null;
  brand: string;
  accent: string;
  background: string;
  text: string;
}

interface BrandThemeFallback {
  colors?: BrandColors;
  fonts?: BrandFonts;
}

type InheritanceSource = "brand" | "override" | "preset";

interface FieldInheritance {
  source: InheritanceSource;
  brandValue: string | undefined;
}

export interface ThemeInheritanceMap {
  "colors.brand": FieldInheritance;
  "colors.accent": FieldInheritance;
  "colors.background": FieldInheritance;
  "colors.text": FieldInheritance;
  "colors.secondary": FieldInheritance;
  "colors.surface": FieldInheritance;
  "colors.muted": FieldInheritance;
  "colors.brandDark": FieldInheritance;
  "fonts.heading": FieldInheritance;
  "fonts.body": FieldInheritance;
}

export interface ResolvedBrandTheme {
  theme: SiteTheme;
  inheritance: ThemeInheritanceMap;
  hasAnyBrandInheritance: boolean;
}

export interface ThemeInheritanceSummary {
  brandDefaultCount: number;
  siteOverrideCount: number;
  presetFallbackCount: number;
  overriddenFields: string[];
}

const THEME_INHERITANCE_LABELS: Record<keyof ThemeInheritanceMap, string> = {
  "colors.brand": "Primary / brand",
  "colors.accent": "Accent",
  "colors.background": "Background",
  "colors.text": "Text",
  "colors.secondary": "Secondary",
  "colors.surface": "Surface",
  "colors.muted": "Muted",
  "colors.brandDark": "Primary dark",
  "fonts.heading": "Heading font",
  "fonts.body": "Body font",
};

function buildFieldInheritance(
  overrideValue: string | undefined,
  brandValue: string | undefined,
): FieldInheritance {
  if (overrideValue && overrideValue.trim().length > 0) {
    return { source: "override", brandValue };
  }
  if (brandValue && brandValue.trim().length > 0) {
    return { source: "brand", brandValue };
  }
  return { source: "preset", brandValue: undefined };
}

export function resolveBrandInheritance(
  siteOverrides: SiteTheme,
  brand: BrandThemeFallback | null,
): ResolvedBrandTheme {
  const inheritance: ThemeInheritanceMap = {
    "colors.brand": buildFieldInheritance(siteOverrides.colors?.brand, brand?.colors?.primary),
    "colors.accent": buildFieldInheritance(siteOverrides.colors?.accent, brand?.colors?.accent),
    "colors.background": buildFieldInheritance(siteOverrides.colors?.background, brand?.colors?.background),
    "colors.text": buildFieldInheritance(siteOverrides.colors?.text, brand?.colors?.text),
    "colors.secondary": buildFieldInheritance(siteOverrides.colors?.secondary, undefined),
    "colors.surface": buildFieldInheritance(siteOverrides.colors?.surface, undefined),
    "colors.muted": buildFieldInheritance(siteOverrides.colors?.muted, undefined),
    "colors.brandDark": buildFieldInheritance(siteOverrides.colors?.brandDark, undefined),
    "fonts.heading": buildFieldInheritance(
      siteOverrides.fonts?.heading?.family,
      brand?.fonts?.headings?.family,
    ),
    "fonts.body": buildFieldInheritance(
      siteOverrides.fonts?.body?.family,
      brand?.fonts?.body?.family,
    ),
  };

  const hasAnyBrandInheritance = Object.values(inheritance).some(
    (field) => field.source === "brand",
  );

  return {
    theme: buildPreviewTheme(siteOverrides, brand),
    inheritance,
    hasAnyBrandInheritance,
  };
}

export function summarizeThemeInheritance(
  inheritance: ThemeInheritanceMap,
): ThemeInheritanceSummary {
  const entries = Object.entries(inheritance) as Array<
    [keyof ThemeInheritanceMap, ThemeInheritanceMap[keyof ThemeInheritanceMap]]
  >;

  return entries.reduce<ThemeInheritanceSummary>(
    (summary, [key, field]) => {
      if (field.source === "brand") {
        summary.brandDefaultCount += 1;
      } else if (field.source === "override") {
        summary.siteOverrideCount += 1;
        summary.overriddenFields.push(THEME_INHERITANCE_LABELS[key]);
      } else {
        summary.presetFallbackCount += 1;
      }

      return summary;
    },
    {
      brandDefaultCount: 0,
      siteOverrideCount: 0,
      presetFallbackCount: 0,
      overriddenFields: [],
    },
  );
}

function buildBrandFont(
  font: BrandFonts["headings"] | BrandFonts["body"] | undefined,
) {
  if (!font?.family) {
    return undefined;
  }

  return {
    family: font.family,
    googleFont: font.family,
    ...(font.weight ? { weight: font.weight } : {}),
  };
}

function getMoodPreset(fontMoodId: FontMoodId) {
  const mood = FONT_MOOD_OPTIONS.find((option) => option.id === fontMoodId) ?? FONT_MOOD_OPTIONS[0];
  const preset = SITE_THEME_PRESETS.find((candidate) => candidate.id === mood.presetId);
  if (preset) {
    return preset;
  }

  return {
    id: "modern",
    name: "Modern",
    description: "Clean SaaS defaults with crisp blues and neutral surfaces.",
    theme: normalizeSiteTheme(SITE_THEME_PRESETS[0]?.theme),
  } as const;
}

export function detectFontMood(theme: SiteTheme | null | undefined): FontMoodId {
  if (!theme) {
    return "clean";
  }

  const presetId = theme.presetId;
  switch (presetId) {
    case "classic":
      return "elegant";
    case "bold":
      return "confident";
    case "minimal":
      return "calm";
    case "playful":
      return "friendly";
    case "modern":
      return "clean";
    default:
      return "clean";
  }
}

export function buildQuickSiteTheme(input: QuickThemeInput): SiteTheme {
  if (input.templateVertical) {
    return buildSimpleSiteTheme({
      vertical: input.templateVertical,
      colors: {
        brand: input.brand,
        accent: input.accent,
        background: input.background,
        text: input.text,
      },
    });
  }

  const preset = getMoodPreset(input.fontMoodId ?? "clean");

  return normalizeSiteTheme({
    presetId: preset.id,
    spacing: SIMPLE_EDITOR_DEFAULTS.sectionSpacing,
    borderRadius: SIMPLE_EDITOR_DEFAULTS.cardRadius,
    buttonStyle: "solid",
    cardStyle: "shadow",
    colors: {
      brand: input.brand,
      accent: input.accent,
      background: input.background,
      text: input.text,
      surface: SIMPLE_EDITOR_DEFAULTS.cardBackground,
    },
  });
}

export function buildPreviewTheme(
  theme: SiteTheme,
  brand: BrandThemeFallback | null,
): SiteTheme {
  if (!brand) {
    return normalizeSiteTheme(theme);
  }

  return normalizeSiteTheme({
    ...theme,
    colors: {
      brand: brand.colors?.primary,
      accent: brand.colors?.accent,
      background: brand.colors?.background,
      text: brand.colors?.text,
      ...(theme.colors ?? {}),
    },
    fonts: {
      heading: buildBrandFont(brand.fonts?.headings),
      body: buildBrandFont(brand.fonts?.body),
      ...(theme.fonts ?? {}),
    },
  });
}

export function useSiteTheme(siteId: string | undefined) {
  const [theme, setTheme] = useState<SiteTheme | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!siteId) {
      setTheme(null);
      return;
    }

    let isCancelled = false;

    setIsLoading(true);

    apiFetch<{ data: { globalTheme?: SiteTheme | null } }>(`/crm/pages/sites/${siteId}`)
      .then((res) => {
        if (isCancelled) {
          return;
        }

        setTheme(
          res.data?.globalTheme
            ? sanitizeSiteTheme(res.data.globalTheme)
            : sanitizeSiteTheme({ presetId: SITE_THEME_PRESETS[0]?.id }),
        );
      })
      .catch(() => {
        if (isCancelled) {
          return;
        }

        setTheme(sanitizeSiteTheme({ presetId: SITE_THEME_PRESETS[0]?.id }));
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [siteId]);

  const saveTheme = useCallback(async (newTheme: SiteTheme) => {
    if (!siteId) return;
    const pagesUrl = import.meta.env.VITE_PAGES_URL || "";
    const normalizedTheme = sanitizeSiteTheme(newTheme);
    await apiFetch(`${pagesUrl}/crm/pages/sites/${siteId}`, {
      method: "PUT",
      body: JSON.stringify({ globalTheme: normalizedTheme }),
    });
    setTheme(normalizedTheme);
  }, [siteId]);

  return { theme, isLoading, saveTheme, setTheme };
}

export function themeToCSS(theme: SiteTheme): string {
  const normalizedTheme = normalizeSiteTheme(theme);
  const safeColor = (value: string | undefined, fallback: string) => (
    typeof value === "string" && /^[#(),.%\sa-zA-Z0-9-]+$/.test(value.trim())
      ? value.trim()
      : fallback
  );
  const safeFont = (value: string | undefined, fallback: string) => (
    typeof value === "string" && /^[a-zA-Z0-9 \-]+$/.test(value.trim())
      ? value.trim()
      : fallback
  );
  const safeDimension = (value: string | undefined, fallback: string) => (
    typeof value === "string" && /^(\d+(\.\d+)?(px|rem|em|%)|0)$/.test(value.trim())
      ? value.trim()
      : fallback
  );
  return `:root {
  --site-primary: ${safeColor(normalizedTheme.colors?.brand, "#1040CC")};
  --site-secondary: ${safeColor(normalizedTheme.colors?.secondary ?? normalizedTheme.colors?.surface, "#F8FAFC")};
  --site-accent: ${safeColor(normalizedTheme.colors?.accent, "#F05E0A")};
  --site-bg: ${safeColor(normalizedTheme.colors?.background, "#FFFFFF")};
  --site-fg: ${safeColor(normalizedTheme.colors?.text, "#0F172A")};
  --site-muted: ${safeColor(normalizedTheme.colors?.muted, "#E2E8F0")};
  --site-font-heading: '${safeFont(normalizedTheme.fonts?.heading?.family, "Inter")}', sans-serif;
  --site-font-body: '${safeFont(normalizedTheme.fonts?.body?.family, "Inter")}', sans-serif;
  --site-radius: ${safeDimension(normalizedTheme.borderRadius, "12px")};
  --site-spacing: ${safeDimension(normalizedTheme.spacing, "4rem")};
}`;
}
